import { useEffect, useState, useCallback } from "react";
import { Plus, X, Check, RotateCcw, LayoutGrid, List as ListIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useLanguage } from "../../context/LanguageContext";
import { tCrm, crmApi, formatDate, TASK_TYPE_META } from "../../lib/crm";
import { toast } from "sonner";

const TASK_TYPES = ["call", "email", "whatsapp", "meeting", "proposal", "wait", "note"];

export default function CRMTasks({ me }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const [scope, setScope] = useState("today");
  const [tasks, setTasks] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [calendarView, setCalendarView] = useState(false);

  const fetch = useCallback(async () => {
    const params = {};
    if (scope === "today" || scope === "week") params.scope = scope;
    const r = await crmApi.get("/tasks", { params });
    setTasks(r.data);
    setLoading(false);
  }, [scope]);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleDone = async (task) => {
    const newStatus = task.status === "done" ? "pending" : "done";
    try {
      await crmApi.put(`/tasks/${task.id}`, { status: newStatus });
      fetch();
    } catch {
      toast.error(t("common.error"));
    }
  };

  const now = Date.now();
  const overdue = tasks.filter((t) => t.status === "pending" && new Date(t.due_date).getTime() < now);
  const pending = tasks.filter((t) => t.status === "pending" && new Date(t.due_date).getTime() >= now);
  const done = tasks.filter((t) => t.status === "done");

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("tasks.title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white/5 rounded-md p-1">
            {["today", "week", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setScope(s)}
                data-testid={`tasks-scope-${s}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded ${
                  scope === s ? "bg-lime-400 text-white" : "text-white/60 hover:text-white"
                }`}
              >
                {t(`tasks.${s}`)}
              </button>
            ))}
          </div>
          {scope === "week" && (
            <div className="flex border border-white/10 rounded-md overflow-hidden">
              <button
                onClick={() => setCalendarView(false)}
                data-testid="tasks-view-list"
                className={`px-2 py-2 ${!calendarView ? "bg-white/10" : "hover:bg-white/5"}`}
                title="List view"
              >
                <ListIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCalendarView(true)}
                data-testid="tasks-view-calendar"
                className={`px-2 py-2 ${calendarView ? "bg-white/10" : "hover:bg-white/5"}`}
                title="Calendar view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
          )}
          <Button onClick={() => setShowNew(true)} data-testid="new-task-btn" className="bg-lime-400 hover:bg-lime-300 text-white">
            <Plus className="w-4 h-4 mr-1" /> {t("tasks.newTask")}
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="text-white/40 text-sm">{t("common.loading")}</div>
      ) : scope === "week" && calendarView ? (
        <WeekCalendar tasks={tasks} onToggle={toggleDone} />
      ) : tasks.length === 0 ? (
        <div className="text-white/40 text-sm">{t("tasks.noTasks")}</div>
      ) : (
        <>
          {overdue.length > 0 && (
            <Section title={t("tasks.overdue")} color="red">
              {overdue.map((task) => <TaskRow key={task.id} task={task} onToggle={toggleDone} overdue />)}
            </Section>
          )}
          {pending.length > 0 && (
            <Section title="Pending" color="blue">
              {pending.map((task) => <TaskRow key={task.id} task={task} onToggle={toggleDone} />)}
            </Section>
          )}
          {done.length > 0 && (
            <Section title="Done" color="green">
              {done.map((task) => <TaskRow key={task.id} task={task} onToggle={toggleDone} />)}
            </Section>
          )}
        </>
      )}

      {showNew && <NewTaskDialog onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); fetch(); }} />}
    </div>
  );
}

function WeekCalendar({ tasks, onToggle }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return d;
  });
  const dayLabel = (d) => d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  const isoDay = (d) => d.toISOString().slice(0, 10);
  const tasksByDay = {};
  for (const t of tasks) {
    const key = t.due_date.slice(0, 10);
    if (!tasksByDay[key]) tasksByDay[key] = [];
    tasksByDay[key].push(t);
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="week-calendar">
      {days.map((d) => {
        const key = isoDay(d);
        const dayTasks = tasksByDay[key] || [];
        const isToday = key === isoDay(today);
        return (
          <div
            key={key}
            className={`rounded-xl border p-3 min-h-[180px] ${
              isToday ? "border-lime-400/40 bg-lime-400/5" : "border-white/8 bg-white/[0.02]"
            }`}
            data-testid={`calendar-day-${key}`}
          >
            <div className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2 flex items-center justify-between">
              <span>{dayLabel(d)}</span>
              {dayTasks.length > 0 && <span className="text-white/40 mono">{dayTasks.length}</span>}
            </div>
            <div className="space-y-1.5">
              {dayTasks.length === 0 ? (
                <div className="text-[11px] text-white/20">—</div>
              ) : (
                dayTasks.map((tsk) => {
                  const meta = TASK_TYPE_META[tsk.task_type] || {};
                  return (
                    <div
                      key={tsk.id}
                      onClick={() => onToggle(tsk)}
                      data-testid={`cal-task-${tsk.id}`}
                      className={`flex items-start gap-2 p-2 rounded-md text-xs cursor-pointer transition ${
                        tsk.status === "done" ? "bg-white/[0.02] opacity-50" : "bg-white/5 hover:bg-white/10"
                      }`}
                      style={{ borderLeft: `2px solid ${meta.color || "#94a3b8"}` }}
                    >
                      <span>{meta.icon || "📝"}</span>
                      <span className={`flex-1 ${tsk.status === "done" ? "line-through" : ""}`}>{tsk.title}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Section({ title, color, children }) {
  const bgColor = color === "red" ? "bg-red-500/5 border-red-500/20" : color === "green" ? "bg-emerald-500/5 border-emerald-500/20" : "bg-white/[0.02] border-white/8";
  return (
    <div>
      <h3 className="text-xs uppercase tracking-wider text-white/40 mb-2">{title}</h3>
      <div className={`rounded-xl border ${bgColor} divide-y divide-white/5`}>{children}</div>
    </div>
  );
}

function TaskRow({ task, onToggle, overdue }) {
  const meta = TASK_TYPE_META[task.task_type] || {};
  return (
    <div className="flex items-center gap-3 p-4" data-testid={`task-row-${task.id}`}>
      <button onClick={() => onToggle(task)} data-testid={`task-toggle-${task.id}`} className="w-5 h-5 rounded border border-white/30 flex items-center justify-center hover:border-lime-400 transition">
        {task.status === "done" && <Check className="w-3 h-3 text-emerald-400" />}
      </button>
      <span className="text-xl">{meta.icon || "📝"}</span>
      <div className="flex-1">
        <div className={`text-sm font-medium ${task.status === "done" ? "line-through text-white/40" : ""}`}>{task.title}</div>
        {task.description && <div className="text-xs text-white/40 mt-0.5">{task.description}</div>}
      </div>
      <div className={`text-xs mono ${overdue ? "text-red-400" : "text-white/40"}`}>{formatDate(task.due_date)}</div>
    </div>
  );
}

function NewTaskDialog({ onClose, onCreated }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const [form, setForm] = useState({
    task_type: "call",
    title: "",
    description: "",
    due_date: new Date().toISOString().slice(0, 10),
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await crmApi.post("/tasks", { ...form, due_date: new Date(form.due_date).toISOString() });
      toast.success("Task created");
      onCreated();
    } catch {
      toast.error(t("common.error"));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-strong rounded-2xl p-6 w-full max-w-md border border-white/10">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">{t("tasks.newTask")}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-white/70 text-xs">{t("tasks.type")}</Label>
            <select
              value={form.task_type}
              onChange={(e) => setForm({ ...form, task_type: e.target.value })}
              data-testid="task-type-select"
              className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
            >
              {TASK_TYPES.map((tt) => (
                <option key={tt} value={tt}>{TASK_TYPE_META[tt].icon} {tt}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-white/70 text-xs">{t("tasks.title2")}</Label>
            <Input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="task-title-input"
              className="bg-white/5 border-white/10 text-white mt-1"
            />
          </div>
          <div>
            <Label className="text-white/70 text-xs">{t("tasks.due")}</Label>
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              data-testid="task-due-input"
              className="bg-white/5 border-white/10 text-white mt-1"
            />
          </div>
          <div>
            <Label className="text-white/70 text-xs">{t("tasks.description")}</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-white/5 border-white/10 text-white mt-1"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-white/10 text-white/80">{t("common.cancel")}</Button>
            <Button type="submit" data-testid="task-submit-btn" className="flex-1 bg-lime-400 hover:bg-lime-300 text-white">
              {t("common.create")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
